import React from 'react'
import { motion } from 'framer-motion'
import { ArrowRight, Cpu, Zap, Lock, HardDrive, Monitor } from 'lucide-react'
import melhoradorLogo from '../../../../assets/images/Melhorador-logo.png'

export default function Hero() {
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative flex flex-col items-center justify-center min-h-screen px-4 py-20 overflow-hidden text-center bg-black">
      
      {/* Background Elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyber-blue/10 rounded-full blur-[100px] animate-pulse-slow"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-[100px] animate-pulse-slow delay-1000"></div>
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>
      </div>

      {/* Logo/Icon Container */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="mb-8 relative"
      >
        <div className="w-40 h-40 md:w-56 md:h-56 mx-auto relative z-10 flex items-center justify-center">
          <img 
            src={melhoradorLogo} 
            alt="Logo O Melhorador Desktop" 
            className="w-full h-full object-contain drop-shadow-[0_0_30px_rgba(0,243,255,0.4)]"
          />
        </div>
        {/* Glow effect behind logo */}
        <div className="absolute inset-0 bg-cyber-blue blur-[80px] opacity-30 z-0"></div>
      </motion.div>

      {/* Headlines */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.8 }}
      >
        <div className="inline-block px-4 py-1.5 mb-6 rounded-full border border-cyber-blue/30 bg-cyber-blue/10 text-cyber-blue text-xs md:text-sm font-bold tracking-widest uppercase backdrop-blur-md">
          Potência Máxima • GPU Local
        </div>
        
        <h1 className="mb-6 text-4xl font-black text-white md:text-6xl lg:text-7xl tracking-tighter leading-none">
          O MELHORADOR <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyber-blue via-white to-purple-500 text-glow-blue">
            DESKTOP PRO
          </span>
        </h1>
      </motion.div>

      <motion.p 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.8 }}
        className="max-w-3xl mb-10 text-base text-gray-400 md:text-xl leading-relaxed font-light mx-auto"
      >
        A ferramenta definitiva para quem busca <span className="text-white font-medium">performance extrema</span>. 
        Utilize sua própria GPU para processar imagens ilimitadas, com total privacidade e sem depender da nuvem.
      </motion.p>

      {/* CTA Button */}
      <motion.button
        whileHover={{ scale: 1.02, boxShadow: "0 0 30px rgba(0, 243, 255, 0.4)" }}
        whileTap={{ scale: 0.98 }}
        onClick={scrollToPricing}
        className="relative px-12 py-5 text-lg font-bold tracking-wider text-black uppercase transition-all duration-300 rounded-xl bg-gradient-to-r from-cyber-blue to-purple-500 font-display group shadow-[0_0_20px_rgba(0,243,255,0.2)]"
      >
        <span className="relative z-10 flex items-center gap-3">
          BAIXAR AGORA <ArrowRight className="w-5 h-5" />
        </span>
        <div className="absolute inset-0 transition-opacity opacity-0 rounded-xl bg-white/40 group-hover:opacity-100 blur-sm mix-blend-overlay"></div>
      </motion.button>

      {/* Tech Specs */}
      <motion.div 
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.8 }}
        className="flex flex-wrap justify-center gap-6 md:gap-12 mt-16 border-t border-white/5 pt-10"
      >
        <SpecItem icon={<Cpu className="w-5 h-5 text-cyber-blue" />} text="Uso de GPU" />
        <SpecItem icon={<Zap className="w-5 h-5 text-yellow-400" />} text="Ilimitado" />
        <SpecItem icon={<Lock className="w-5 h-5 text-green-400" />} text="100% Privado" />
        <SpecItem icon={<HardDrive className="w-5 h-5 text-purple-400" />} text="Offline Mode" />
      </motion.div>

    </section>
  )
}

const SpecItem = ({ icon, text }: { icon: React.ReactNode, text: string }) => (
  <div className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors">
    <div className="p-2 rounded-lg bg-white/5 border border-white/5">
      {icon}
    </div>
    <span className="text-sm font-medium uppercase tracking-wider">{text}</span>
  </div>
)
